import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import {
  EIntakeStep,
  IApplication,
} from "@rentiohq/shared-frontend/dist/types/application.types";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { isFormattedBody } from "@rentiohq/web-shared/dist/components/RichTextInput/richText.utils";
import { getApplicationLocale } from "utils/application.utils";
import {
  getFeedbackFieldForLocale,
  IExtraData,
} from "./schema.propertyApplicationNotEligible.types";

/**
 * The languages actually being written to, in the order the candidates stand,
 * with how many of them read each.
 *
 * Only the languages in the selection: a box for French on a screen of Dutch
 * candidates is a box that can never be sent, and three boxes to move one
 * candidate would be three times the reading for no choice at all.
 */
export const getLocalesInSelection = (
  applications: IApplication[],
): { locale: string; count: number }[] => {
  const counts: { [locale: string]: number } = {};

  applications.forEach(application => {
    const locale = getApplicationLocale(application);
    counts[locale] = (counts[locale] || 0) + 1;
  });

  return Object.keys(counts).map(locale => ({
    locale,
    count: counts[locale],
  }));
};

/**
 * The message this broker set for this step in one language, ready to be read
 * and edited.
 *
 * Everything that is the same for every reader is filled in, because the broker
 * is checking a message rather than proofreading a template. The recipient's
 * name is the one thing left as a marker: the send resolves it per candidate,
 * so one text can greet several people each by their own name.
 *
 * It stays a marker even where only one candidate reads this language. Filling
 * it in there would put a name in one box and a marker in the one below it, and
 * a broker comparing the two would fairly wonder which of them is going to be
 * personal.
 */
const buildTenantFeedbackText = (
  extraData: IExtraData,
  locale: string,
): string | undefined => {
  const { localeMessages, user, property, applications } = extraData;

  const localeMessage = localeMessages[locale];

  if (!localeMessage) {
    return undefined;
  }

  const params: { [key: string]: string | null | undefined } = {
    address: formatAddress(property),
    applicant: "{{applicants}}",
    actor: applications[0]?.broker?.name || getName(user),

    propertyId: `${property.id}`,
    propertyInfoId: `${property.propertyInfoId}`,

    propertyReference: property.reference,
    propertyDescription: property.description,
    propertyName: property.name,

    refDewaele: property.refDewaele,
    refMaxImmo: property.refMaxImmo,
    refOmnicasa: property.refOmnicasa,
    refRealSuite: property.refRealSuite,
    refSkarabee: property.refSkarabee,
    refSweepbright: property.refSweepbright,
    refWhise: property.refWhise,
    refZabun: property.refZabun,

    // NOTE: {{bookingUrl}} and {{noInterestUrl}} are intentionally left
    // unresolved. The backend detects them and renders the "book a visit"
    // button and the "no longer interested" link, so they must survive to the
    // API as-is.
  };

  let result = localeMessage;
  Object.keys(params).forEach(key => {
    result = result.replaceAll(`{{${key}}}`, params[key] || "");
  });

  return result;
};

export const getInitialValues = (extraData: IExtraData) => {
  const values: { [field: string]: string | undefined } = {};

  getLocalesInSelection(extraData.applications).forEach(({ locale }) => {
    values[getFeedbackFieldForLocale(locale)] = buildTenantFeedbackText(
      extraData,
      locale,
    );
  });

  return values;
};

/**
 * What each candidate is sent, keyed by application id.
 *
 * Whatever stands in the box for that candidate's language, edited by the
 * broker or not. A language the broker left empty, because the office never
 * set a message for it, sends nothing to those candidates, exactly as before.
 */
export const getTenantFeedbackTextPerApplication = (
  extraData: IExtraData,
  formData: { [field: string]: any },
): { [applicationId: string]: string | undefined } => {
  const result: { [applicationId: string]: string | undefined } = {};

  extraData.applications.forEach(application => {
    const field = getFeedbackFieldForLocale(getApplicationLocale(application));
    const text = formData[field];

    result[application.id] = text === "" ? undefined : text;
  });

  return result;
};

/**
 * The placeholder a broker's text must keep, per step.
 *
 * Some of these mails are not an announcement but an errand: the candidate is
 * asked to do something, and the placeholder is where the link to do it is
 * rendered. A broker rewriting the body is free to say it their own way, but
 * if they drop the placeholder the mail still goes out and simply has no way
 * forward in it, which nobody notices until a candidate says they never got a
 * link.
 *
 * A step that is absent here is an announcement and needs nothing.
 *
 * `exactlyOnce` is not pedantry. The candidacy mail is assembled by splitting
 * the broker's text around the marker into text, button, text, so a second
 * marker would produce a shape the sender cannot build. The invitation is not
 * split that way, so a repeat there is merely odd rather than broken, and
 * refusing it would invalidate texts offices may already have saved.
 */
export interface IMarkerRule {
  marker: string;
  exactlyOnce: boolean;
  /**
   * Whether the text must carry the marker at all. The booking and candidacy
   * links are the candidate's way forward, so dropping them is refused. The
   * no-interest link is a way out an office may leave out on purpose: an
   * office text without it simply sends no such link (Jonas, 2026-10-07).
   */
  isRequired: boolean;
  /**
   * The mail's own call-to-action key. The warning names the button the marker
   * turns into, so the broker recognizes the thing they are about to break
   * rather than being told about "a link" (Jonas, 2026-10-02).
   */
  buttonKey: string;
  /**
   * Wording for a marker that becomes a link rather than a button. The default
   * messages say "button", which would describe the wrong thing.
   */
  messageByProblem?: Partial<Record<TMarkerProblem, string>>;
}

/**
 * The "I am no longer interested" link (DEV-3330). One rule, used by the
 * step-change modal and by both settings tabs that offer it, so the variable
 * has a single spelling everywhere.
 *
 * Optional, and allowed more than once: the sender swaps each one for the link
 * in place rather than splitting the text around it. It must stand on a line
 * of its own, because the link replaces the whole line.
 */
export const NO_INTEREST_MARKER_RULE: IMarkerRule = {
  marker: "{{noInterestUrl}}",
  exactlyOnce: false,
  isRequired: false,
  buttonKey: "email.rentmatch_no_interest.cta.title",
  messageByProblem: {
    not_alone: "property.application.feedback.marker_alone_link",
  },
};

/**
 * The booking button on the invitation: the candidate's way forward, so the
 * text must keep it. Repeats are odd rather than broken there.
 */
export const BOOKING_MARKER_RULE: IMarkerRule = {
  marker: "{{bookingUrl}}",
  exactlyOnce: false,
  isRequired: true,
  buttonKey: "email.rentmatch_invited.cta.title",
};

/**
 * The same marker in the booking reminder (DEV-3330, Jonas 2026-10-07): it
 * says where the button goes, so the no-interest link can sit under it.
 *
 * Optional, because without it the button simply follows the text. Once,
 * because the reminder splits the text around it into the blocks above and
 * below the button, and a second marker gives a shape it cannot build. Named
 * after the reminder's own button, which is labeled differently.
 */
export const REMINDER_BOOKING_MARKER_RULE: IMarkerRule = {
  ...BOOKING_MARKER_RULE,
  exactlyOnce: true,
  isRequired: false,
  buttonKey: "email.rentmatch_booking_reminder.cta.title",
};

export const MARKER_RULES_BY_STEP: { [step: number]: IMarkerRule[] } = {
  [EIntakeStep.invited]: [BOOKING_MARKER_RULE, NO_INTEREST_MARKER_RULE],
  [EIntakeStep.candidateTenant]: [
    {
      marker: "{{candidacyUrl}}",
      exactlyOnce: true,
      isRequired: true,
      buttonKey: "email.rentmatch_candidate_tenant_invite.cta.title",
    },
  ],
};

export const getMarkerRules = (step: EIntakeStep): IMarkerRule[] =>
  MARKER_RULES_BY_STEP[step] || [];

export type TMarkerProblem = "missing" | "repeated" | "not_alone";

/**
 * Whether the marker stands alone in its own paragraph.
 *
 * The sender splits the body at the marker and renders a button between the
 * halves. With formatting that split would cut through a block and leave an
 * opening tag with no close on one side and a close with no open on the
 * other, so the marker is required to be a paragraph of its own and the save
 * is refused otherwise (DEV-3323). Checking it here, while the broker is
 * looking at their text, beats re-balancing fragments at send time.
 *
 * In plain text the same rule reads as a line containing nothing else.
 *
 * Formatted or not is decided by the editor's own rule rather than by a
 * second copy of it here, so the two cannot drift: a broker who typed
 * "3 < 5" in a plain text is not told their text is markup.
 */
export const isMarkerAlone = (text: string, marker: string) => {
  if (!isFormattedBody(text)) {
    return text.split(/\r?\n/).some(line => line.trim() === marker);
  }

  // A paragraph holding the marker and nothing besides it, allowing the empty
  // inline wrappers an editor leaves behind. Only a paragraph counts: the
  // sender splits the body there, and a heading or a list item carrying the
  // marker would leave the sender a fragment it cannot close.
  const blocks = text.split(/<\/p>/i);
  return blocks.some(block => {
    const withoutTags = block.replace(/<[^>]*>/g, "").trim();
    return withoutTags === marker;
  });
};

/**
 * The message for each problem, so both screens say the same thing. Keyed on
 * the union rather than on `string`, so adding a problem without a message
 * fails to compile instead of rendering the word "undefined" to a broker.
 */
export const MESSAGE_BY_PROBLEM: Record<TMarkerProblem, string> = {
  missing: "property.application.feedback.marker_required",
  repeated: "property.application.feedback.marker_once",
  not_alone: "property.application.feedback.marker_alone",
};

/** One problem, with the rule it broke, so its message can name that rule. */
export interface IMarkerFinding {
  problem: TMarkerProblem;
  rule: IMarkerRule;
}

/** The sentence a broker reads for one finding, in both screens alike. */
export const getMarkerFindingMessage = (finding: IMarkerFinding) =>
  getLocalizedText(
    finding.rule.messageByProblem?.[finding.problem] ||
      MESSAGE_BY_PROBLEM[finding.problem],
    {
      marker: finding.rule.marker,
      button: getLocalizedText(finding.rule.buttonKey),
    },
  );

/**
 * What is wrong with the marker in one text, if anything.
 *
 * Kept apart from the form-shaped check below so the settings screen can
 * use the identical rule. It had its own save path and no check at all
 * until DEV-3323, which meant a broker could write a text there that the
 * step-change modal would refuse unchanged.
 *
 * An empty text is not judged: a language left blank on purpose belongs to
 * the required rule rather than to this one. An optional marker that is
 * absent is not a problem either.
 */
export const getMarkerProblem = (
  text: string,
  rule: Pick<IMarkerRule, "marker" | "exactlyOnce"> &
    Partial<Pick<IMarkerRule, "isRequired">>,
): TMarkerProblem | undefined => {
  if (!text) {
    return undefined;
  }

  const count = text.split(rule.marker).length - 1;

  if (count === 0) {
    return rule.isRequired === false ? undefined : "missing";
  }

  if (count > 1 && rule.exactlyOnce) {
    return "repeated";
  }

  if (!isMarkerAlone(text, rule.marker)) {
    return "not_alone";
  }

  return undefined;
};

/** The first rule a text breaks, in the order the rules are listed. */
export const getMarkerFinding = (
  text: string,
  rules: IMarkerRule[],
): IMarkerFinding | undefined => {
  for (const rule of rules) {
    const problem = getMarkerProblem(text, rule);

    if (problem) {
      return { problem, rule };
    }
  }

  return undefined;
};

/** The same check, over every language field the step-change modal shows. */
export const getMarkerProblems = (
  extraData: IExtraData,
  formData: { [field: string]: string } | undefined,
): ({ field: string } & IMarkerFinding)[] => {
  const rules = getMarkerRules(extraData.step);

  if (rules.length === 0) {
    return [];
  }

  const problems: ({ field: string } & IMarkerFinding)[] = [];

  getLocalesInSelection(extraData.applications).forEach(({ locale }) => {
    const field = getFeedbackFieldForLocale(locale);
    const finding = getMarkerFinding(formData?.[field] || "", rules);

    if (finding) {
      problems.push({ field, ...finding });
    }
  });

  return problems;
};
